import { useEffect, useMemo, useRef, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import {
  AlertOctagon,
  Bot,
  CheckCircle2,
  ClipboardList,
  CornerDownLeft,
  History as HistoryIcon,
  Loader2,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  User as UserIcon,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useAssistant, KIND_LABEL, type ChatMessage, type ProposedAction } from '@/store/assistant';
import { armConfirm } from '@/lib/armConfirm';
import { cn } from '@/lib/utils';
import { rootRoute } from './__root';

function AssistantWorkspace() {
  const messages = useAssistant((s) => s.messages);
  const audit = useAssistant((s) => s.audit);
  const draft = useAssistant((s) => s.draft);
  const setDraft = useAssistant((s) => s.setDraft);
  const sendUser = useAssistant((s) => s.sendUser);
  const busy = useAssistant((s) => s.busy);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, busy]);

  return (
    <div className="flex h-full bg-base">
      <QuickActionsRail />
      <main className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center gap-2 border-b border-border-default bg-surface-1/95 px-4 py-2.5 backdrop-blur-md">
          <Bot className="h-4 w-4 text-accent" />
          <h1 className="text-[14px] font-semibold tracking-tight text-fg-primary">Diva</h1>
          <Badge tone="accent">confirm-before-apply</Badge>
          <span className="ml-auto font-mono text-[10.5px] text-fg-muted">
            rule-based copilot · LLM backend later
          </span>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-base px-4 py-4">
          <div className="mx-auto max-w-[820px] space-y-3">
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-[11px] text-fg-muted">
                <Loader2 className="h-3 w-3 animate-spin text-accent" />
                generating…
              </div>
            )}
          </div>
        </div>

        <Composer
          draft={draft}
          busy={busy}
          onChange={setDraft}
          onSend={() => sendUser(draft)}
        />
      </main>
      <AuditPanel audit={audit} />
    </div>
  );
}

/* =================================================================== */
/* Quick actions rail                                                   */
/* =================================================================== */

function QuickActionsRail() {
  const sendUser = useAssistant((s) => s.sendUser);
  const reset = useAssistant((s) => s.resetConversation);
  const actions = [
    { label: 'Status report', icon: ClipboardList, prompt: 'status' },
    { label: 'Arm', icon: ShieldCheck, prompt: 'arm' },
    { label: 'Take off to 15 m', icon: Sparkles, prompt: 'take off to 15' },
    { label: 'Hold position', icon: AlertOctagon, prompt: 'hold' },
    { label: 'Set speed 8 m/s', icon: Settings2, prompt: 'set speed 8' },
    { label: 'Return home (RTL)', icon: CornerDownLeft, prompt: 'rtl' },
    { label: 'Land', icon: AlertOctagon, prompt: 'land' },
  ];
  return (
    <aside
      className="flex h-full flex-none flex-col overflow-y-auto overflow-x-hidden border-r border-border-default bg-surface-1"
      style={{ width: 'clamp(220px, 18vw, 280px)' }}
    >
      <header className="border-b border-border-default px-3 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          <h2 className="text-[12px] font-semibold tracking-tight text-fg-primary">Quick prompts</h2>
        </div>
        <p className="mt-0.5 text-[10.5px] text-fg-muted">
          Press to send. Or type your own below.
        </p>
      </header>
      <ul className="flex-1 space-y-1 p-2">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <li key={a.label}>
              <button
                type="button"
                onClick={() => sendUser(a.prompt)}
                className="flex w-full items-start gap-2 rounded border border-border-subtle bg-surface-2/40 px-2.5 py-2 text-left text-[11.5px] text-fg-secondary transition-colors hover:border-accent/30 hover:bg-accent/5 hover:text-fg-primary"
              >
                <Icon className="mt-0.5 h-3 w-3 flex-none text-accent" />
                <span>{a.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <footer className="border-t border-border-subtle px-3 py-2">
        <button
          type="button"
          onClick={() => {
            if (armConfirm('diva-reset', 'Reset conversation? Audit log is preserved.')) reset();
          }}
          className="w-full font-mono text-[10px] uppercase tracking-wider text-fg-muted hover:text-fg-secondary"
        >
          Reset conversation
        </button>
      </footer>
    </aside>
  );
}

/* =================================================================== */
/* Message bubbles                                                      */
/* =================================================================== */

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="grid h-7 w-7 flex-none place-items-center rounded-full border border-accent/40 bg-accent/15 text-accent">
          <Bot className="h-3.5 w-3.5" />
        </div>
      )}
      <div
        className={cn(
          'max-w-[78%] rounded-lg border px-3 py-2 text-[12.5px] leading-relaxed',
          isUser
            ? 'border-border-default bg-surface-2 text-fg-primary'
            : 'border-border-subtle bg-surface-1 text-fg-primary',
        )}
      >
        <MarkdownLight body={message.body} />
        <div className="mt-1.5 flex items-center justify-between gap-2 font-mono text-[9.5px] text-fg-muted">
          <span>{isUser ? 'you' : 'assistant'}</span>
          <span>{fmtTime(message.ts)}</span>
        </div>
        {message.action && <ActionCard messageId={message.id} action={message.action} />}
      </div>
      {isUser && (
        <div className="grid h-7 w-7 flex-none place-items-center rounded-full border border-border-default bg-surface-2 text-fg-secondary">
          <UserIcon className="h-3.5 w-3.5" />
        </div>
      )}
    </div>
  );
}

/** Bare-bones markdown: bold via **…**, line breaks, headings via `## ` and `**…**` lines. */
function MarkdownLight({ body }: { body: string }) {
  const lines = body.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (line.startsWith('## ')) {
          return (
            <h3 key={i} className="text-[13px] font-semibold tracking-tight text-fg-primary">
              {line.slice(3)}
            </h3>
          );
        }
        if (line.trim() === '') return <div key={i} className="h-1" />;
        return (
          <p key={i} className="text-[12.5px] text-fg-primary">
            {renderInline(line)}
          </p>
        );
      })}
    </div>
  );
}

function renderInline(line: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  // Match **bold** and `code` non-greedily.
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(line))) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    const tok = m[0]!;
    if (tok.startsWith('**')) {
      parts.push(
        <strong key={i++} className="text-fg-primary">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else {
      parts.push(
        <code key={i++} className="rounded bg-surface-3 px-1 py-0.5 font-mono text-[11px] text-fg-secondary">
          {tok.slice(1, -1)}
        </code>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < line.length) parts.push(line.slice(last));
  return parts;
}

/* =================================================================== */
/* Proposed action card                                                 */
/* =================================================================== */

function ActionCard({ messageId, action }: { messageId: string; action: ProposedAction }) {
  const confirm = useAssistant((s) => s.confirm);
  const reject = useAssistant((s) => s.reject);
  return (
    <div
      className={cn(
        'mt-2 rounded border p-2.5',
        action.status === 'pending' && 'border-amber-500/40 bg-amber-500/5',
        action.status === 'confirmed' && 'border-status-good/40 bg-status-good/5',
        action.status === 'rejected' && 'border-status-critical/30 bg-status-critical/5 opacity-70',
      )}
    >
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-accent" />
        <span className="text-[10.5px] uppercase tracking-wider text-fg-muted">
          Proposed {KIND_LABEL[action.kind]}
        </span>
        {action.status === 'confirmed' && <Badge tone="good">confirmed</Badge>}
        {action.status === 'rejected' && <Badge tone="critical">rejected</Badge>}
      </div>
      <div className="mt-1 text-[12px] font-semibold text-fg-primary">{action.title}</div>
      {action.changes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {action.changes.map((c) => (
            <li
              key={c.key}
              className="flex items-center gap-2 font-mono text-[10.5px]"
            >
              <span className="text-fg-secondary">{c.key}</span>
              <span className="text-fg-muted line-through">{c.from}</span>
              <CornerDownLeft className="h-3 w-3 text-fg-muted" />
              <span className="text-accent">{c.to}</span>
            </li>
          ))}
        </ul>
      )}
      {action.status === 'pending' && (
        <div className="mt-2 flex items-center gap-1.5">
          <Button size="sm" onClick={() => confirm(messageId)}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Confirm
          </Button>
          <Button size="sm" variant="ghost" onClick={() => reject(messageId)}>
            <X className="h-3.5 w-3.5" />
            Reject
          </Button>
          <span className="ml-auto text-[10px] text-fg-muted">Logged to audit on either path.</span>
        </div>
      )}
    </div>
  );
}

/* =================================================================== */
/* Composer                                                             */
/* =================================================================== */

function Composer({
  draft,
  busy,
  onChange,
  onSend,
}: {
  draft: string;
  busy: boolean;
  onChange: (s: string) => void;
  onSend: () => void;
}) {
  return (
    <div className="border-t border-border-default bg-surface-1 p-3">
      <div className="mx-auto flex max-w-[820px] items-end gap-2 rounded-lg border border-border-default bg-base px-3 py-2 focus-within:border-accent">
        <textarea
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!busy) onSend();
            }
          }}
          rows={1}
          placeholder='Try: "take off to 15" · "go to 28.5450, 77.1926" · "set speed 8" · "status"'
          className="max-h-32 min-h-[28px] flex-1 resize-none bg-transparent text-[13px] text-fg-primary placeholder:text-fg-muted outline-none"
        />
        <Button size="sm" disabled={busy || !draft.trim()} onClick={onSend}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send
        </Button>
      </div>
      <p className="mx-auto mt-1 max-w-[820px] text-center font-mono text-[9.5px] uppercase tracking-wider text-fg-muted">
        Enter to send · Shift+Enter for newline · Every proposed change requires confirm
      </p>
    </div>
  );
}

/* =================================================================== */
/* Audit log                                                            */
/* =================================================================== */

function AuditPanel({ audit }: { audit: ReturnType<typeof useAssistant.getState>['audit'] }) {
  const stats = useMemo(() => {
    const confirmed = audit.filter((a) => a.result === 'confirmed').length;
    const rejected = audit.filter((a) => a.result === 'rejected').length;
    return { confirmed, rejected };
  }, [audit]);

  return (
    <aside
      className="flex h-full flex-none flex-col overflow-y-auto overflow-x-hidden border-l border-border-default bg-surface-1"
      style={{ width: 'clamp(280px, 22vw, 340px)' }}
    >
      <header className="border-b border-border-default px-3 py-3">
        <div className="flex items-center gap-2">
          <HistoryIcon className="h-4 w-4 text-accent" />
          <h2 className="text-[12px] font-semibold tracking-tight text-fg-primary">Audit log</h2>
          <Badge tone="accent">{audit.length}</Badge>
        </div>
        <p className="mt-0.5 text-[10.5px] text-fg-muted">
          Every accepted or rejected action through the assistant.
        </p>
        <div className="mt-2 flex gap-1.5">
          <span className="rounded border border-status-good/40 bg-status-good/10 px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wider text-status-good">
            {stats.confirmed} confirmed
          </span>
          <span className="rounded border border-status-critical/40 bg-status-critical/10 px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wider text-status-critical">
            {stats.rejected} rejected
          </span>
        </div>
      </header>
      <ul className="flex-1 divide-y divide-border-subtle">
        {audit.map((entry) => (
          <li key={entry.id} className="px-3 py-2">
            <div className="flex items-center gap-1.5">
              {entry.result === 'confirmed' ? (
                <CheckCircle2 className="h-3 w-3 text-status-good" />
              ) : (
                <X className="h-3 w-3 text-status-critical" />
              )}
              <span className="font-mono text-[10px] tabular-nums text-fg-muted">
                {fmtFullTs(entry.ts)}
              </span>
              <Badge tone={entry.result === 'confirmed' ? 'good' : 'critical'}>
                {entry.result}
              </Badge>
            </div>
            <div className="mt-0.5 text-[11.5px] text-fg-primary">{entry.summary}</div>
            <div className="mt-0.5 font-mono text-[9.5px] text-fg-muted">
              {KIND_LABEL[entry.kind]} · by @{entry.actor}
            </div>
          </li>
        ))}
        {audit.length === 0 && (
          <li className="px-3 py-6 text-center text-[11px] text-fg-muted">
            No actions logged yet.
          </li>
        )}
      </ul>
      <footer className="border-t border-border-subtle bg-surface-2/40 px-3 py-2 text-[10px] leading-snug text-fg-muted">
        <ShieldCheck className="mr-1 inline h-3 w-3 text-status-good" />
        Audit persists locally in this browser.
      </footer>
    </aside>
  );
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
function fmtFullTs(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

export const assistantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assistant',
  component: AssistantWorkspace,
});
